import React, { useRef, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  MenuItem,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useForm, Controller } from 'react-hook-form';
import Dropzone from 'react-dropzone';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import FileUploadStatus, {
  FileUploadStatusEnum,
} from '@features/workspace/views/document/modals/upload-documents-modal/file-upload-status';
import { DocumentTypeEnum } from '@utils/constants';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  selectActiveCompany,
  selectEmployees,
  selectPrimaryUser,
} from '@features/workspace/store/selectors';
import clsx from 'clsx';
import { SleepingCowImage } from '@app/icons';
import { connectApi } from '@app/api';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { fetchCompanyDocuments } from '@features/workspace/store/actions';

const schema = yup.object().shape({
  documentType: yup.number().min(0, 'Please select document type').required(),
  userId: yup
    .string()
    .transform((v) => v.trim())
    .when('documentType', {
      is: DocumentTypeEnum.PersonalAddressRegistration,
      then: yup.string().required('Please select customer'),
    }),
});

const useStyles = makeStyles<any>()({
  root: {
    display: 'flex',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    minWidth: 800,
    background: '#FFFFFF',
    borderRadius: '30px',
    overflow: 'hidden',
  },
  leftSide: {
    display: 'flex',
    flexBasis: '50%',
    padding: '3rem',
  },
  rightSide: {
    display: 'flex',
    flexBasis: '50%',
    background: '#F5F6FA',
    padding: '74px 55px 45px 55px',
  },
  dropzoneContainer: {
    borderRadius: '30px',
    backgroundColor: '#3E7B9A',
    cursor: 'default',
    '&.disabled': {
      backgroundColor: '#B0B8C1',
      cursor: 'not-allowed',
    },
  },
});

interface IFile {
  file: any;
  companyId: string;
  documentType: number;
  userId: string | null;
  contactName: string | null;
  uploadProgress: number | null;
  uploadStatus: number;
  documentId: string | null;
  expiryDate: Date;
}

export const documentTypeOptions = [
  {
    value: DocumentTypeEnum.BusinessAddressRegistration,
    label: 'Proof of business address',
  },
  {
    value: DocumentTypeEnum.BusinessRegistration,
    label: 'Business registration',
  },
  {
    value: DocumentTypeEnum.BusinessOrganogram,
    label: 'Business organogram',
  },
  {
    value: DocumentTypeEnum.PersonalAddressRegistration,
    label: 'Customer proof of address',
  },
  {
    value: DocumentTypeEnum.TemporaryID,
    label: 'Temporary identity document',
  },
  {
    value: DocumentTypeEnum.SourceOfFunds,
    label: 'Source of funds',
  },
  {
    value: DocumentTypeEnum.SourceOfWealth,
    label: 'Source of wealth',
  },
];

const INIT_DATA = {
  documentType: -1,
  // White space inside is intentionally, cuz TextField doesn't show empty string option as default
  userId: ' ',
  expiryDate: new Date(),
};

const Index: React.FC<any> = ({ open, onClose }) => {
  const { classes } = useStyles(null);

  const dispatch = useAppDispatch();

  const employees = useAppSelector(selectEmployees);
  const company = useAppSelector(selectActiveCompany);
  const primaryUser = useAppSelector(selectPrimaryUser);

  const { control, watch, setValue, reset } = useForm({
    defaultValues: INIT_DATA,
    resolver: yupResolver(schema),
  });

  const formData = watch();

  useEffect(() => {
    if (
      (formData.documentType === DocumentTypeEnum.PersonalAddressRegistration ||
        formData.documentType === DocumentTypeEnum.TemporaryID ||
        formData.documentType === DocumentTypeEnum.SourceOfFunds ||
        formData.documentType === DocumentTypeEnum.SourceOfWealth) &&
      employees.length === 1
    ) {
      setValue('userId', employees[0].userId);
    } else {
      setValue('userId', ' ');
    }
    }, [formData.documentType]) // eslint-disable-line

  const isUploading = useRef<boolean>(false);

  const [files, setFiles] = useState<Array<IFile>>([]);

  const [fileProgressPipe, setFileProgressPipe] = useState<any>(null);

  // Reflect progress from scoped callback to realtime state
  useEffect(() => {
    if (fileProgressPipe) {
      if (files[fileProgressPipe.iFile]) {
        console.debug(
          `fileProgressPipe: ${fileProgressPipe.iFile} >> ${fileProgressPipe.progress}`
        );
        const updateData: any = {
          uploadProgress: fileProgressPipe.progress,
        };

        if (fileProgressPipe.progress === 100) {
          updateData.uploadStatus = FileUploadStatusEnum.Success;
        }
        updateFile(fileProgressPipe.iFile, updateData);
      }
    }
    }, [fileProgressPipe]) // eslint-disable-line

  const onUploadProgress = (iFile: number) => (event: ProgressEvent) => {
    const progress = Math.floor((event.loaded / event.total) * 100);

    setFileProgressPipe({
      iFile,
      progress,
    });
  };

  const updateFile = (iFile: number, data: any) => {
    if (!files[iFile]) return;
    const newFiles = [...files];

    newFiles[iFile] = {
      ...newFiles[iFile],
      ...data,
    };
    setFiles(newFiles);
  };

  useEffect(() => {
    // console.debug('Watch Files:', files)
  }, [files]);

  const upload = (iFile: number, file: IFile): Promise<any> => {
    const fd = new FormData();

    fd.append('files', file.file);
    fd.append('companyId', file.companyId);
    fd.append('documentType', file.documentType.toString());
    if (file.documentType === DocumentTypeEnum.PersonalAddressRegistration) {
      fd.append('userId', file.companyId);
    }

    if (file.documentType === DocumentTypeEnum.TemporaryID) {
      fd.append('expiryDate', file.expiryDate.toISOString());
    }

    const fileUploadPromise =
      file.documentType === DocumentTypeEnum.PersonalAddressRegistration ||
      file.documentType === DocumentTypeEnum.SourceOfFunds ||
      file.documentType === DocumentTypeEnum.SourceOfWealth ||
      file.documentType === DocumentTypeEnum.TemporaryID
        ? connectApi.document.upload(
            file.userId || '',
            fd,
            onUploadProgress(iFile)
          )
        : connectApi.document.upload(
            primaryUser?.id ?? '',
            fd,
            onUploadProgress(iFile)
          );

    fileUploadPromise.then((res: any) =>
      setFiles((oldFiles) => {
        const newFiles = [...oldFiles];

        newFiles[iFile].documentId = res?.data?.payload?.[0]?.id;

        return newFiles;
      })
    );

    fileUploadPromise.catch(() =>
      setFiles((oldFiles) => {
        const newFiles = [...oldFiles];

        newFiles[iFile].uploadStatus = FileUploadStatusEnum.Failed;
        isUploading.current = false;

        return newFiles;
      })
    );

    return fileUploadPromise;
  };

  const onDrop = async (acceptedFiles: any) => {
    isUploading.current = true;
    const uploadPromises: Array<Promise<any>> = [];
    const newFiles = [...files];

    acceptedFiles.forEach((f: any) => {
      const file: IFile = {
        documentId: null,
        file: f,
        companyId: company?.id ?? '',
        documentType: formData.documentType,
        uploadStatus: FileUploadStatusEnum.Pending,
        uploadProgress: 5,
        userId: formData.userId,
        contactName: formData.userId
          ? customerOptions.find((opt: any) => opt.value === formData.userId)
              ?.label ?? null
          : null,
        expiryDate: formData.expiryDate,
      };

      const iFile = newFiles.push(file) - 1;

      const fileUploadPromise = upload(iFile, file);

      uploadPromises.push(fileUploadPromise);
    });
    setFiles(newFiles);
    // Reset form
    await Promise.all(uploadPromises);
    reset(INIT_DATA);
    isUploading.current = false;
  };

  const customerOptions = useMemo(
    () =>
      employees?.map(
        (e: any) =>
          ({
            value: e.userId,
            label: [e.person.title, e.person.firstName, e.person.lastName]
              .filter((np) => !!np)
              .join(' '),
          } || [])
      ),
    [employees]
  );

  const onRetry = (iFile: number) => () => {
    // Reset file state
    const newFiles = [...files];

    newFiles[iFile] = {
      ...newFiles[iFile],
      uploadStatus: FileUploadStatusEnum.Pending,
      uploadProgress: 5,
    };
    setFiles(newFiles);
    // Start uploading
    const file = files[iFile];

    upload(iFile, file);
  };

  const onDelete = (iFile: number) => () => {
    const newFiles = [...files];
    const fileToDelete = files[iFile];

    newFiles.splice(iFile, 1);
    if (fileToDelete.documentId) {
      connectApi.document.delete(fileToDelete.documentId);
    }
    setFiles(newFiles);
  };

  const onFinish = () => {
    setFiles([]);
    dispatch(fetchCompanyDocuments(null));
    onClose();
  };

  const isDropzoneEnabled = useMemo(() => {
    return (
      !isUploading.current &&
      formData.documentType !== -1 &&
      (formData.documentType !== DocumentTypeEnum.PersonalAddressRegistration ||
        !!formData.userId.trim())
    );
    }, [formData, isUploading.current]) // eslint-disable-line

  return (
    <Modal
      aria-describedby="upload-documents"
      aria-labelledby="upload-documents"
      open={open}
      onClose={onClose}
    >
      <Box className={classes.root}>
        <Box className={classes.leftSide}>
          <Box display="flex" flexDirection="column">
            <Box display="flex">
              <Typography variant="legend">Upload document</Typography>
            </Box>
            <Box display="flex" mt={2.5}>
              <Alert severity="info" style={{ width: '100%' }}>
                Please note: Ensure the correct document type is selected prior
                to upload.
              </Alert>
            </Box>
            {/* Document Type */}
            <Controller
              control={control}
              name="documentType"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TextField
                  fullWidth
                  required
                  select
                  disabled={isUploading.current}
                  error={!!error}
                  helperText={error?.message}
                  label="Document type"
                  value={value}
                  variant="outlined"
                  onChange={onChange}
                >
                  <MenuItem value={-1}>Select</MenuItem>
                  {documentTypeOptions.map((option) => (
                    <MenuItem
                      key={`document-type-option-${option.value}`}
                      value={option.value}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            {/* Expiry Date */}
            {formData.documentType !== -1 &&
              formData.documentType === DocumentTypeEnum.TemporaryID && (
                <Controller
                  control={control}
                  name="expiryDate"
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DesktopDatePicker
                        inputFormat="YYYY-MM-DD"
                        label="Expiry Date"
                        minDate={new Date()}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={!!error}
                            helperText={error?.message}
                          />
                        )}
                        value={value}
                        onChange={onChange}
                      />
                    </LocalizationProvider>
                  )}
                />
              )}

            {/* Customer */}
            {formData.documentType !== -1 &&
              (formData.documentType ===
                DocumentTypeEnum.PersonalAddressRegistration ||
                formData.documentType === DocumentTypeEnum.TemporaryID ||
                formData.documentType === DocumentTypeEnum.SourceOfFunds ||
                formData.documentType === DocumentTypeEnum.SourceOfWealth) && (
                <Controller
                  control={control}
                  name="userId"
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <TextField
                      fullWidth
                      required
                      select
                      disabled={isUploading.current}
                      error={!!error}
                      helperText={error?.message}
                      label="Customer"
                      value={value}
                      variant="outlined"
                      onChange={onChange}
                    >
                      {customerOptions.length > 1 && (
                        <MenuItem value={' '}>Select</MenuItem>
                      )}
                      {customerOptions.map((option: any) => (
                        <MenuItem
                          key={`customer-option-${option.value}`}
                          value={option.value}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              )}
            {/* Dropzone */}
            <Box alignItems="flex-end" display="flex" flexGrow={1} py={3}>
              <Dropzone disabled={!isDropzoneEnabled} onDrop={onDrop}>
                {({ getRootProps, getInputProps }: any) => (
                  <Box
                    className={clsx(
                      classes.dropzoneContainer,
                      !isDropzoneEnabled && 'disabled'
                    )}
                    display="flex"
                    flexDirection="column"
                    flexGrow={1}
                    height="155px"
                    justifyContent="center"
                    p={3}
                    {...getRootProps()}
                  >
                    <Box
                      alignItems="center"
                      display="flex"
                      justifyContent="center"
                    >
                      <Box
                        alignItems="center"
                        display="flex"
                        justifyContent="center"
                        style={{
                          height: '46px',
                          width: '46px',
                          borderRadius: '23px',
                          backgroundColor: '#B0B8C1AA',
                          filter:
                            'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
                        }}
                      >
                        <Box
                          alignItems="center"
                          display="flex"
                          justifyContent="center"
                          style={{
                            height: '28px',
                            width: '28px',
                            borderRadius: '14px',
                            backgroundColor: '#FFDB9E',
                            boxShadow: '0px 6px 35px rgba(26, 23, 27, 0.2)',
                          }}
                        >
                          <i
                            className="ri-upload-line"
                            style={{ fontSize: 12 }}
                          />
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      alignItems="center"
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      mt={2}
                    >
                      <input {...getInputProps()} />
                      <span
                        style={{
                          fontWeight: '400',
                          fontSize: '14px',
                          lineHeight: '20px',
                          color: '#FFFFFF',
                          opacity: 0.6,
                          cursor: 'default',
                        }}
                      >
                        Drag and drop your files here or
                      </span>
                      <Button
                        disabled={!isDropzoneEnabled}
                        style={{
                          fontWeight: 700,
                          fontSize: '14px',
                          lineHeight: '22px',
                          textDecoration: 'underline',
                          color: '#FFFFFF',
                        }}
                        variant="text"
                      >
                        Browse files
                      </Button>
                    </Box>
                  </Box>
                )}
              </Dropzone>
            </Box>
          </Box>
        </Box>
        <Box className={classes.rightSide}>
          <Box display="flex" flexDirection="column" width="100%">
            <Box
              display="flex"
              flexDirection="column"
              flexGrow={1}
              minHeight="min(calc(100vh - 225px), 530px)"
            >
              <Card
                style={{
                  height: '100%',
                  padding: '32px 24px 32px 56px',
                }}
              >
                {!!files.length && (
                  <Box
                    style={{
                      maxHeight: '400px',
                      overflowY: 'scroll',
                    }}
                  >
                    {files.map((f: any, iFile: number) => (
                      <React.Fragment key={`${f.file.name}-${iFile}`}>
                        {!!iFile && <Divider />}
                        <Box display="flex" mr={4} my={2}>
                          <FileUploadStatus
                            contactName={f.contactName}
                            documentType={f.documentType}
                            filename={f.file.name}
                            progress={f.uploadProgress}
                            status={f.uploadStatus}
                            onDelete={onDelete(iFile)}
                            onRetry={onRetry(iFile)}
                          />
                        </Box>
                      </React.Fragment>
                    ))}
                  </Box>
                )}

                {!files.length && (
                  <Box
                    alignItems="center"
                    display="flex"
                    flexGrow={1}
                    height="100%"
                  >
                    <Box
                      alignItems="center"
                      display="flex"
                      flexDirection="column"
                      flexGrow={1}
                    >
                      <Box>
                        <Typography style={{ color: '#4B4B4B' }}>
                          There are currently no uploaded documents to show.
                        </Typography>
                      </Box>
                      <Box>
                        <SleepingCowImage height="170px" width="170px" />
                      </Box>
                    </Box>
                  </Box>
                )}
              </Card>
            </Box>
            <Box display="flex" justifyContent="flex-end" mt="46px">
              <Button
                autoFocus
                size="large"
                type="submit"
                variant="contained"
                onClick={onFinish}
              >
                Submit
              </Button>
            </Box>
          </Box>
        </Box>
        <Box
          style={{
            position: 'absolute',
            top: '30px',
            right: '30px',
            cursor: 'pointer',
          }}
          onClick={onClose}
        >
          <i className="ri-close-fill" style={{ fontSize: 24 }} />
        </Box>
      </Box>
    </Modal>
  );
};

export default Index;
